import React, { useState, useRef } from "react";
import { Form, Button, Alert, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

const OTPInput = ({ length = 6, otp, setOtp }) => {
  const inputs = useRef([]);

  const handleChange = (e, index) => {
    const { value } = e.target;

    // Only allow single digit input
    if (value.match(/^\d$/)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move focus to the next input
      if (index < length - 1) {
        inputs.current[index + 1].focus();
      }
    } else if (value === "") {
      // Clear the current input
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);

      // Move focus to previous input on backspace if current input is empty
      if (index > 0) {
        inputs.current[index - 1].focus();
      }
    }
  };

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      {otp.map((digit, index) => (
        <input
          key={index}
          type="text"
          maxLength="1"
          value={digit}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          ref={(el) => (inputs.current[index] = el)}
          style={{
            width: "40px",
            height: "40px",
            margin: "0 5px",
            textAlign: "center",
            fontSize: "18px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        />
      ))}
    </div>
  );
};

const Login = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState(Array(6).fill("")); // OTP input state
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [otpErrorMessage, setOTPErrorMessage] = useState("");
  const [otpsuccessMessage, setOTPSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false); // Track if OTP has been sent
  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  // Handle sending phone number for OTP
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (phoneNumber.length !== 10) {
      setErrorMessage("Phone number must be exactly 10 digits");
      return;
    }
    setLoading(true); // Show loader
    try {
      const response = await fetch(`${BASE_URL}/auth/login-auth`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "https://api.bossinvestor.in",
          "Access-Control-Allow-Credentials": "true",
        },
        body: JSON.stringify({ phoneNumber }),
      });

      const result = await response.json();
      setLoading(false); // Hide loader

      // Check if the response is a success
      if (!response.ok) {
        const errorDetails =
          result.status?.errors?.phoneNumber || result.status.message;
        setErrorMessage(
          errorDetails || "Failed to send OTP. Please try again.",
        );
        return;
      }

      // If OTP request is successful, show OTP input fields
      setOtpSent(true);
      setErrorMessage("");
      setSuccessMessage("OTP sent successfully!");
    } catch (error) {
      setLoading(false); // Hide loader
      setErrorMessage(error.message || "An unexpected error occurred");
    }
  };

  // Handle OTP verification
  const handleOtpVerify = async (e) => {
    e.preventDefault();
    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      setOTPErrorMessage("OTP must be exactly 6 digits");
      return;
    }
    setLoading(true); // Show loader
    try {
      const response = await fetch(`${BASE_URL}/auth/verifyLoginOtp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "https://api.bossinvestor.in",
          "Access-Control-Allow-Credentials": "true",
        },
        body: JSON.stringify({ phoneNumber, otp: otpCode }),
      });

      const result = await response.json();
      setLoading(false); // Hide loader

      if (!response.ok || result?.status?.statusCode !== 200) {
        throw new Error(result?.status?.message || "OTP verification failed.");
      }

      setOTPSuccessMessage("Login successful!");
      setOTPErrorMessage("");
      window.sessionStorage.setItem("jwtToken", result.data.jwtToken);
      window.sessionStorage.setItem("userId", result.data.user.id);
      window.sessionStorage.setItem("refreshToken", result.data.refreshToken);
      window.sessionStorage.setItem(
        "userName",
        result.data.user.firstName + " " + result.data.user.lastName,
      );
      navigate("/homePage", {
        replace: true,
        state: { sessionExpired: false },
      });
    } catch (error) {
      setLoading(false); // Hide loader
      setOTPErrorMessage(error.message || "OTP verification failed.");
    }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!otpSent ? (
          <form onSubmit={handleLoginSubmit} style={{ textAlign: "center" }}>
            <h5>Enter your mobile number</h5>
            <input
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Mobile Number"
              style={{
                width: "250px",
                padding: "10px",
                margin: "10px 0",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            />
            <Button variant="primary" type="submit" style={{ width: "250px" }}>
              {loading ? <Spinner animation="border" size="sm" /> : "Send OTP"}
            </Button>
            {errorMessage && (
              <p variant="danger" className="mt-3 text-danger">
                {errorMessage}
              </p>
            )}
            {successMessage && (
              <p variant="success" className="mt-3 text-danger">
                {successMessage}
              </p>
            )}
            {otpSent && otpsuccessMessage && (
              <p variant="success" className="mt-3 text-danger">
                {otpsuccessMessage}
              </p>
            )}
          </form>
        ) : (
          <div>
            <h5>Mobile Phone Verification</h5>
            <p>
              Enter the 6-digit verification code that was sent to your mobile
              number
            </p>
            <OTPInput length={6} otp={otp} setOtp={setOtp} />
            <Button
              variant="primary"
              type="submit"
              onClick={handleOtpVerify}
              style={{
                width: "250px",
                marginTop: "20px",
                alignItems: "center",
              }}
            >
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Verify OTP"
              )}
            </Button>
            {otpErrorMessage && (
              <p variant="danger" className="mt-3 text-danger">
                {otpErrorMessage}
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Login;
